import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import { CloseApplicationType } from "@common/types/closeApplicationType";
import { CloseHealth } from "@common/types/closeHealth";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import type { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import type {
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewWorkspaceRow,
} from "../clientsWorkOverviewViewConfig";

/** Sample Work stages and rows with random ids, for the Work board's tests and the board playground. */
export const createWorkStage = ({
    name,
    order,
    parent,
}: {
    name: string;
    order: number;
    parent?: OrganizationCloseStage;
}): OrganizationCloseStage => {
    const id = crypto.randomUUID();
    return {
        id,
        name,
        organizationId: crypto.randomUUID(),
        order,
        rootCloseStageId: parent?.rootCloseStageId ?? id,
        parentCloseStageId: parent?.id ?? null,
        createdByUserId: null,
        createdAt: new Date(),
        updatedByUserId: null,
        updatedAt: new Date(),
    };
};

export const createWorkMember = (
    preferredName: string,
): CloseOverviewMember => ({
    userId: crypto.getRandomValues(new Uint32Array(1))[0],
    role: UserWorkspaceRole.ACCOUNTANT,
    email: `${crypto.randomUUID()}@example.com`,
    iconColor: "blue",
    avatar: null,
    preferredName,
    organizationId: crypto.randomUUID(),
});

export const createWorkRow = (
    overrides: Partial<ClientsWorkOverviewWorkRow>,
): ClientsWorkOverviewWorkRow => ({
    id: crypto.randomUUID(),
    rowType: "work",
    name: "Monthly close",
    entityId: crypto.getRandomValues(new Uint32Array(1))[0],
    workspaceId: crypto.randomUUID(),
    entityIconColor: "blue",
    entityLogo: null,
    entityName: "Acme LLC",
    entityIsBusiness: true,
    workspaceName: "Acme",
    closeId: `close:${crypto.randomUUID()}`,
    startDate: null,
    endDate: null,
    workStartDate: null,
    workDeliveryDate: null,
    closeStageId: null,
    closeStageName: null,
    closeTemplateId: crypto.randomUUID(),
    recurrenceId: null,
    closeStageSetAt: null,
    isFinished: false,
    cancelledAt: null,
    terminatedTasksCount: 0,
    totalTaskCount: 0,
    estimatedTimeRemaining: 0,
    estimatedTimeDone: 0,
    estimatedTimeByMember: [],
    preparers: [],
    reviewers: [],
    managers: [],
    openRoles: {
        preparer: { assigneeIds: [], hasOpenTasks: true },
        reviewer: { assigneeIds: [], hasOpenTasks: true },
        manager: { assigneeIds: [], hasOpenTasks: true },
    },
    createdByUserId: null,
    createdAt: "2026-10-01T00:00:00.000Z",
    unreadTaskCount: 0,
    applicationType: CloseApplicationType.MANUAL,
    health: CloseHealth.NONE,
    healthReason: null,
    isAgentRunning: false,
    ...overrides,
});

/** A client on the page with its applications, which take its id and name. */
export const createWorkspaceRow = ({
    name,
    children,
}: {
    name: string;
    children: Array<Partial<ClientsWorkOverviewWorkRow>>;
}): ClientsWorkOverviewWorkspaceRow => {
    const workspaceId = crypto.randomUUID();
    return {
        id: `workspace:${workspaceId}`,
        rowType: "workspace",
        name,
        workspaceId,
        children: children.map((child) =>
            createWorkRow({ ...child, workspaceId, workspaceName: name }),
        ),
    };
};
